import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Settings2, ChevronLeft, ChevronRight, List } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useReportTicketList, type ReportTicket } from '@/hooks/useReportTicketList';
import { PageLoading } from '@/components/ui/page-loading';

interface ReportTicketListProps {
  startDate: Date;
  endDate: Date;
  ticketTypes: string[];
  priority: string;
}

interface ColumnConfig {
  key: keyof ReportTicket;
  label: string;
  defaultVisible: boolean;
}

const ALL_COLUMNS: ColumnConfig[] = [
  { key: 'numero', label: 'Número', defaultVisible: true },
  { key: 'titulo', label: 'Título', defaultVisible: true },
  { key: 'tipoLabel', label: 'Tipo', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'prioridade', label: 'Prioridade', defaultVisible: true },
  { key: 'urgencia', label: 'Urgência', defaultVisible: false },
  { key: 'solicitante', label: 'Solicitante', defaultVisible: true },
  { key: 'cliente', label: 'Cliente', defaultVisible: false },
  { key: 'grupo', label: 'Grupo Responsável', defaultVisible: false },
  { key: 'atendente', label: 'Atendente', defaultVisible: true },
  { key: 'categoria', label: 'Categoria', defaultVisible: false },
  { key: 'canal_origem', label: 'Canal de Origem', defaultVisible: false },
  { key: 'data_abertura', label: 'Data Abertura', defaultVisible: true },
  { key: 'data_resolucao', label: 'Data Resolução', defaultVisible: false },
  { key: 'data_fechamento', label: 'Data Fechamento', defaultVisible: false },
  { key: 'sla_status', label: 'SLA', defaultVisible: true },
];

const STORAGE_KEY = 'report-ticket-list-columns';

const statusColorMap: Record<string, string> = {
  aberta: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  em_andamento: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  pendente: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  resolvida: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  fechada: 'bg-muted text-muted-foreground',
  cancelada: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const tipoColorMap: Record<string, string> = {
  Incidente: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  Requisição: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  Problema: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  Mudança: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

const slaColorMap: Record<string, string> = {
  'Cumprido': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  'Dentro do prazo': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  'Violado': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  'N/A': 'bg-muted text-muted-foreground',
};

const prioridadeColorMap: Record<string, string> = {
  critica: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  alta: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  media: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  baixa: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

export const ReportTicketList: React.FC<ReportTicketListProps> = ({
  startDate,
  endDate,
  ticketTypes,
  priority,
}) => {
  const { data: tickets = [], isLoading } = useReportTicketList({ startDate, endDate, ticketTypes, priority });

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return new Set(JSON.parse(saved));
    } catch {}
    return new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
  });

  // Pagination
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...visibleColumns]));
  }, [visibleColumns]);

  useEffect(() => {
    setCurrentPage(0);
  }, [tickets.length, pageSize]);

  const toggleColumn = (key: string) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const activeColumns = ALL_COLUMNS.filter(c => visibleColumns.has(c.key));
  const totalPages = Math.ceil(tickets.length / pageSize);
  const paginatedTickets = tickets.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return '-';
    }
  };

  const renderCell = (ticket: ReportTicket, key: keyof ReportTicket) => {
    const value = ticket[key];

    if (key === 'status') {
      const colorClass = statusColorMap[value as string] || 'bg-muted text-muted-foreground';
      return <Badge variant="outline" className={`${colorClass} border-0 text-xs`}>{(value as string || '').replace('_', ' ')}</Badge>;
    }
    if (key === 'tipoLabel') {
      const colorClass = tipoColorMap[value as string] || 'bg-muted text-muted-foreground';
      return <Badge variant="outline" className={`${colorClass} border-0 text-xs`}>{value as string}</Badge>;
    }
    if (key === 'prioridade') {
      const colorClass = prioridadeColorMap[value as string] || 'bg-muted text-muted-foreground';
      return <Badge variant="outline" className={`${colorClass} border-0 text-xs capitalize`}>{value as string || '-'}</Badge>;
    }
    if (key === 'sla_status') {
      const colorClass = slaColorMap[value as string] || 'bg-muted text-muted-foreground';
      return <Badge variant="outline" className={`${colorClass} border-0 text-xs`}>{value as string}</Badge>;
    }
    if (['data_abertura', 'data_resolucao', 'data_fechamento'].includes(key)) {
      return <span className="text-xs whitespace-nowrap">{formatDate(value as string | null)}</span>;
    }
    if (key === 'titulo') {
      return <span className="max-w-[250px] truncate block text-sm">{value as string}</span>;
    }

    return <span className="text-sm">{(value as string) || '-'}</span>;
  };

  if (isLoading) return <PageLoading message="Carregando chamados..." />;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Lista de Chamados
            </CardTitle>
            <CardDescription>
              {tickets.length} chamado{tickets.length !== 1 ? 's' : ''} encontrado{tickets.length !== 1 ? 's' : ''} no período
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / pág</SelectItem>
                <SelectItem value="25">25 / pág</SelectItem>
                <SelectItem value="50">50 / pág</SelectItem>
                <SelectItem value="100">100 / pág</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Settings2 className="h-4 w-4" />
                  Colunas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Colunas visíveis</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ALL_COLUMNS.map(col => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={visibleColumns.has(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {activeColumns.map(col => (
                  <TableHead key={col.key} className="whitespace-nowrap">{col.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeColumns.length} className="text-center py-8 text-muted-foreground">
                    Nenhum chamado encontrado no período selecionado
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTickets.map(ticket => (
                  <TableRow key={`${ticket.tipo}-${ticket.id}`}>
                    {activeColumns.map(col => (
                      <TableCell key={col.key}>{renderCell(ticket, col.key)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">
              Página {currentPage + 1} de {totalPages} ({tickets.length} registros)
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
